import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AspectRatio,
  Box,
  Center,
  HStack,
  Image,
  Stack,
} from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@outdoorly/button';
import { XIcon } from '@outdoorly/icons';

export const ImageUpload = ({
  value,
  onChange,
  accept = 'image/png,image/jpeg',
  maxFiles,
}) => {
  const images = useMemo(() => value ?? [], [value]);
  const inputFile = useRef(null);

  const onInput = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      const file = event.target.files[0];

      const tempList = [
        ...images,
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          uuid: uuidv4(),
        }),
      ];

      onChange(tempList.slice(Math.max(tempList.length - maxFiles, 0)));
    },
    [onChange, images, maxFiles]
  );

  const removeFile = useCallback(
    (uuid) => {
      URL.revokeObjectURL(images.find((image) => image.uuid === uuid).preview);
      onChange(images.filter((image) => image.uuid !== uuid));
    },
    [onChange, images]
  );

  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      images.forEach((image) => URL.revokeObjectURL(image.preview));
    },
    [images]
  );

  return (
    <Stack>
      {images.length && (
        <HStack justify="center">
          {images.map(
            (image) =>
              image.preview && (
                <Box
                  key={image.uuid}
                  borderColor="scheme.border"
                  borderWidth={2}
                  borderRadius="xl"
                  overflow="hidden"
                  _hover={{
                    cursor: 'pointer',
                    borderColor: 'scheme.controlHover',
                  }}
                  role="button"
                  aria-label="Remove image"
                  transition=".15s"
                  position="relative"
                  onClick={() => removeFile(image.uuid)}
                >
                  <AspectRatio ratio={1} maxW="full" w="md">
                    <Image src={image.preview} objectFit="cover" />
                  </AspectRatio>

                  <Center
                    position="absolute"
                    top={0}
                    bottom={0}
                    width="full"
                    backgroundColor="gray.700"
                    opacity={0}
                    transition="0.15s"
                    _hover={{ opacity: 0.5 }}
                  >
                    <XIcon width={10} height={10} color="white" />
                  </Center>
                </Box>
              )
          )}
        </HStack>
      )}
      <Button
        variant="subtle"
        fontWeight="500"
        size="sm"
        fontSize="sm"
        isLoading={false}
        onClick={() => inputFile.current?.click()}
      >
        Upload Image
      </Button>
      <input
        id="imageInput"
        type="file"
        accept={accept}
        ref={inputFile}
        style={{ display: 'none' }}
        onChange={onInput}
      />
    </Stack>
  );
};

ImageUpload.propTypes = {
  accept: PropTypes.string,
  maxFiles: PropTypes.number,
  onChange: PropTypes.func,
  value: PropTypes.array,
};
